interface CircularProgressProps {
  score: number;
  maxScore?: number;
  size?: "sm" | "md" | "lg";
  strokeWidth?: number;
  showLabels?: boolean;
}

export function CircularProgress({ 
  score, 
  maxScore = 10, 
  size = "md",
  strokeWidth,
  showLabels = false 
}: CircularProgressProps) {
  const percentage = (score / maxScore) * 100;
  
  const sizeConfig = {
    sm: { dimension: 48, defaultStroke: 4, fontSize: "text-sm", labelSize: "text-[8px]" },
    md: { dimension: 100, defaultStroke: 6, fontSize: "text-2xl", labelSize: "text-xs" },
    lg: { dimension: 140, defaultStroke: 8, fontSize: "text-4xl", labelSize: "text-xs" },
  };
  
  const config = sizeConfig[size];
  const stroke = strokeWidth ?? config.defaultStroke;
  const radius = (config.dimension - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: config.dimension, height: config.dimension }}>
        {/* Glow effect */}
        <div 
          className="absolute inset-0 rounded-full bg-cyan/20 blur-md opacity-50"
          style={{ transform: 'scale(1.1)' }}
        />
        
        <svg
          width={config.dimension}
          height={config.dimension}
          className="relative -rotate-90"
        >
          {/* Gradient definition */}
          <defs>
            <linearGradient id={`progress-gradient-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--teal))" />
              <stop offset="100%" stopColor="hsl(var(--cyan))" />
            </linearGradient>
          </defs>
          
          {/* Background circle */}
          <circle
            cx={config.dimension / 2}
            cy={config.dimension / 2}
            r={radius}
            fill="none"
            stroke="hsl(var(--muted) / 0.3)"
            strokeWidth={stroke}
          />
          
          {/* Progress circle */}
          <circle
            cx={config.dimension / 2}
            cy={config.dimension / 2}
            r={radius}
            fill="none"
            stroke={`url(#progress-gradient-${size})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-700 ease-out"
          />
        </svg>
        
        {/* Center score */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${config.fontSize} font-light text-foreground`}>
            {score % 1 === 0 ? score : score.toFixed(1)}
          </span>
        </div>
      </div>
      
      {/* Labels */}
      {showLabels && (
        <div className="flex justify-between w-full px-1">
          <span className={`${config.labelSize} text-muted-foreground`}>0</span>
          <span className={`${config.labelSize} text-muted-foreground`}>{maxScore}</span>
        </div>
      )}
    </div>
  );
}
